import { Input, Select, Checkbox, RadioGroup, Textarea } from '@/shared/components/ui';
import { useState } from 'react';
import styles from './FormsPage.module.css';

export const FormsPage = () => {
    const [inputValue, setInputValue] = useState('');
    const [errorExample, setErrorExample] = useState('');

    // Estados para nuevos componentes
    const [selectValue, setSelectValue] = useState('');
    const [checkboxValue, setCheckboxValue] = useState(false);
    const [checkboxGroup, setCheckboxGroup] = useState({ option1: false, option2: true, option3: false });
    const [radioValue, setRadioValue] = useState('');
    const [textareaValue, setTextareaValue] = useState('');

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Forms</h1>
                <p className={styles.description}>
                    Componentes de formularios reutilizables para inputs, labels y validaciones.
                    Todos los componentes siguen el design system de Zafiro ERP.
                </p>
            </div>

            {/* Input Básico */}
            <section className={styles.section}>
                <h2>Input Básico</h2>
                <p>Input simple sin label ni validaciones.</p>

                <div className={styles.showcase}>
                    <div className={styles.example}>
                        <Input
                            placeholder="Escribe algo..."
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                        />
                        <code className={styles.code}>
                            {`<Input placeholder="Escribe algo..." />`}
                        </code>
                    </div>
                </div>
            </section>

            {/* Input con Label */}
            <section className={styles.section}>
                <h2>Input con Label</h2>
                <p>Input con label descriptivo.</p>

                <div className={styles.showcase}>
                    <div className={styles.example}>
                        <Input
                            id="email-example"
                            label="Correo Electrónico"
                            type="email"
                            placeholder="ejemplo@correo.com"
                        />
                        <code className={styles.code}>
                            {`<Input 
  label="Correo Electrónico"
  type="email"
  placeholder="ejemplo@correo.com"
/>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Input
                            id="password-example"
                            label="Contraseña"
                            type="password"
                            placeholder="••••••••"
                        />
                        <code className={styles.code}>
                            {`<Input 
  label="Contraseña"
  type="password"
  placeholder="••••••••"
/>`}
                        </code>
                    </div>
                </div>
            </section>

            {/* Input con Error */}
            <section className={styles.section}>
                <h2>Input con Validación</h2>
                <p>Input con mensajes de error para validaciones.</p>

                <div className={styles.showcase}>
                    <div className={styles.example}>
                        <Input
                            id="error-example"
                            label="Email con Error"
                            type="email"
                            value={errorExample}
                            onChange={(e) => setErrorExample(e.target.value)}
                            error={errorExample && !errorExample.includes('@') ? 'El email debe contener @' : ''}
                        />
                        <code className={styles.code}>
                            {`<Input 
  label="Email con Error"
  type="email"
  error="El email debe contener @"
/>`}
                        </code>
                        <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                            💡 Escribe un email sin @ para ver el error
                        </p>
                    </div>
                </div>
            </section>

            {/* Tipos de Input */}
            <section className={styles.section}>
                <h2>Tipos de Input</h2>
                <p>Diferentes tipos HTML soportados.</p>

                <div className={styles.showcase}>
                    <div className={styles.example}>
                        <Input
                            label="Texto"
                            type="text"
                            placeholder="Texto normal"
                        />
                        <code className={styles.code}>
                            {`<Input type="text" label="Texto" />`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Input
                            label="Email"
                            type="email"
                            placeholder="email@ejemplo.com"
                        />
                        <code className={styles.code}>
                            {`<Input type="email" label="Email" />`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Input
                            label="Contraseña"
                            type="password"
                            placeholder="••••••••"
                        />
                        <code className={styles.code}>
                            {`<Input type="password" label="Contraseña" />`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Input
                            label="Número"
                            type="number"
                            placeholder="123"
                        />
                        <code className={styles.code}>
                            {`<Input type="number" label="Número" />`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Input
                            label="Teléfono"
                            type="tel"
                            placeholder="+56 9 1234 5678"
                        />
                        <code className={styles.code}>
                            {`<Input type="tel" label="Teléfono" />`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Input
                            label="Fecha"
                            type="date"
                        />
                        <code className={styles.code}>
                            {`<Input type="date" label="Fecha" />`}
                        </code>
                    </div>
                </div>
            </section>

            {/* Estados */}
            <section className={styles.section}>
                <h2>Estados</h2>
                <p>Los inputs pueden estar habilitados o deshabilitados.</p>

                <div className={styles.showcase}>
                    <div className={styles.example}>
                        <Input
                            label="Input Habilitado"
                            placeholder="Puedes escribir aquí"
                        />
                        <code className={styles.code}>
                            {`<Input label="Input Habilitado" />`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Input
                            label="Input Deshabilitado"
                            placeholder="No puedes escribir"
                            disabled
                        />
                        <code className={styles.code}>
                            {`<Input label="Input Deshabilitado" disabled />`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Input
                            label="Input Read-only"
                            value="Valor fijo"
                            readOnly
                        />
                        <code className={styles.code}>
                            {`<Input label="Input Read-only" value="Valor fijo" readOnly />`}
                        </code>
                    </div>
                </div>
            </section>

            {/* Select */}
            <section className={styles.section}>
                <h2>Select</h2>
                <p>Dropdown para seleccionar una opción de una lista.</p>

                <div className={styles.showcase}>
                    <div className={styles.example}>
                        <Select
                            label="País"
                            placeholder="Selecciona un país"
                            options={[
                                { value: 'cl', label: 'Chile' },
                                { value: 'ar', label: 'Argentina' },
                                { value: 'pe', label: 'Perú' },
                                { value: 'co', label: 'Colombia' },
                            ]}
                            value={selectValue}
                            onChange={(value) => setSelectValue(String(value))}
                        />
                        <code className={styles.code}>
                            {`<Select 
  label="País"
  placeholder="Selecciona un país"
  options={[
    { value: 'cl', label: 'Chile' },
    { value: 'ar', label: 'Argentina' }
  ]}
  value={selectValue}
  onChange={setSelectValue}
/>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Select
                            label="Estado"
                            options={[
                                { value: 'active', label: 'Activo' },
                                { value: 'inactive', label: 'Inactivo' },
                            ]}
                            disabled
                        />
                        <code className={styles.code}>
                            {`<Select label="Estado" options={...} disabled />`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Select
                            label="Categoría"
                            placeholder="Selecciona categoría"
                            options={[
                                { value: '1', label: 'Electrónica' },
                                { value: '2', label: 'Ropa' },
                                { value: '3', label: 'Alimentos' },
                            ]}
                            error="Debes seleccionar una categoría"
                        />
                        <code className={styles.code}>
                            {`<Select 
  label="Categoría"
  options={...}
  error="Debes seleccionar una categoría"
/>`}
                        </code>
                    </div>
                </div>
            </section>

            {/* Checkbox */}
            <section className={styles.section}>
                <h2>Checkbox</h2>
                <p>Checkboxes para seleccionar opciones múltiples.</p>

                <div className={styles.showcase}>
                    <div className={styles.example}>
                        <Checkbox
                            label="Acepto los términos y condiciones"
                            checked={checkboxValue}
                            onChange={setCheckboxValue}
                        />
                        <code className={styles.code}>
                            {`<Checkbox 
  label="Acepto los términos"
  checked={checked}
  onChange={setChecked}
/>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Checkbox
                            label="Opción deshabilitada"
                            checked={true}
                            disabled
                        />
                        <code className={styles.code}>
                            {`<Checkbox label="Opción deshabilitada" disabled />`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <h4 style={{ margin: 0, color: 'var(--text-primary)' }}>Intereses</h4>
                            <Checkbox
                                label="Deportes"
                                checked={checkboxGroup.option1}
                                onChange={(checked) => setCheckboxGroup({ ...checkboxGroup, option1: checked })}
                            />
                            <Checkbox
                                label="Música"
                                checked={checkboxGroup.option2}
                                onChange={(checked) => setCheckboxGroup({ ...checkboxGroup, option2: checked })}
                            />
                            <Checkbox
                                label="Tecnología"
                                checked={checkboxGroup.option3}
                                onChange={(checked) => setCheckboxGroup({ ...checkboxGroup, option3: checked })}
                            />
                        </div>
                        <code className={styles.code}>
                            {`<Checkbox label="Option 1" checked={...} />
<Checkbox label="Option 2" checked={...} />
<Checkbox label="Option 3" checked={...} />`}
                        </code>
                    </div>
                </div>
            </section>

            {/* Radio */}
            <section className={styles.section}>
                <h2>Radio Buttons</h2>
                <p>Radio buttons para seleccionar una opción de varias.</p>

                <div className={styles.showcase}>
                    <div className={styles.example}>
                        <RadioGroup
                            label="Plan de Suscripción"
                            name="plan"
                            options={[
                                { value: 'basic', label: 'Básico - $10/mes' },
                                { value: 'pro', label: 'Pro - $20/mes' },
                                { value: 'enterprise', label: 'Enterprise - $50/mes' },
                            ]}
                            value={radioValue}
                            onChange={(value) => setRadioValue(String(value))}
                            direction="vertical"
                        />
                        <code className={styles.code}>
                            {`<RadioGroup 
  label="Plan de Suscripción"
  name="plan"
  options={[...]}
  value={value}
  onChange={setValue}
  direction="vertical"
/>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <RadioGroup
                            label="Método de Pago"
                            name="payment"
                            options={[
                                { value: 'credit', label: 'Tarjeta' },
                                { value: 'paypal', label: 'PayPal' },
                                { value: 'transfer', label: 'Transferencia' },
                            ]}
                            direction="horizontal"
                        />
                        <code className={styles.code}>
                            {`<RadioGroup 
  name="payment"
  options={[...]}
  direction="horizontal"
/>`}
                        </code>
                    </div>
                </div>
            </section>

            {/* Textarea */}
            <section className={styles.section}>
                <h2>Textarea</h2>
                <p>Área de texto para contenido multilínea.</p>

                <div className={styles.showcase}>
                    <div className={styles.example}>
                        <Textarea
                            label="Descripción"
                            placeholder="Escribe una descripción detallada..."
                            value={textareaValue}
                            onChange={(e) => setTextareaValue(e.target.value)}
                            rows={4}
                        />
                        <code className={styles.code}>
                            {`<Textarea 
  label="Descripción"
  placeholder="Escribe..."
  rows={4}
/>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Textarea
                            label="Comentarios"
                            placeholder="Tus comentarios aquí..."
                            helperText="Máximo 500 caracteres"
                            rows={3}
                            resize="vertical"
                        />
                        <code className={styles.code}>
                            {`<Textarea 
  label="Comentarios"
  helperText="Máximo 500 caracteres"
  resize="vertical"
/>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Textarea
                            label="Mensaje de Error"
                            placeholder="Escribe algo..."
                            error="Este campo es requerido"
                            rows={3}
                        />
                        <code className={styles.code}>
                            {`<Textarea 
  label="Mensaje"
  error="Este campo es requerido"
/>`}
                        </code>
                    </div>
                </div>
            </section>

            {/* Ejemplo de Formulario */}
            <section className={styles.section}>
                <h2>Ejemplo: Formulario Completo</h2>

                <div className={styles.formExample}>
                    <form className={styles.form}>
                        <Input
                            id="form-nombre"
                            label="Nombre Completo"
                            type="text"
                            placeholder="Juan Pérez"
                            required
                        />

                        <Input
                            id="form-email"
                            label="Correo Electrónico"
                            type="email"
                            placeholder="juan@ejemplo.com"
                            required
                        />

                        <Input
                            id="form-telefono"
                            label="Teléfono"
                            type="tel"
                            placeholder="+56 9 1234 5678"
                        />

                        <Input
                            id="form-empresa"
                            label="Empresa"
                            type="text"
                            placeholder="Empresa S.A."
                        />
                    </form>

                    <code className={styles.code}>
                        {`<form>
  <Input 
    label="Nombre Completo"
    type="text"
    required
  />
  <Input 
    label="Correo Electrónico"
    type="email"
    required
  />
  <Input 
    label="Teléfono"
    type="tel"
  />
  <Input 
    label="Empresa"
    type="text"
  />
</form>`}
                    </code>
                </div>
            </section>

            {/* Props */}
            <section className={styles.section}>
                <h2>Props</h2>
                <div className={styles.propsTable}>
                    <table>
                        <thead>
                            <tr>
                                <th>Prop</th>
                                <th>Tipo</th>
                                <th>Default</th>
                                <th>Descripción</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>label</code></td>
                                <td><code>string</code></td>
                                <td>-</td>
                                <td>Label descriptivo del input</td>
                            </tr>
                            <tr>
                                <td><code>error</code></td>
                                <td><code>string</code></td>
                                <td>-</td>
                                <td>Mensaje de error a mostrar</td>
                            </tr>
                            <tr>
                                <td><code>type</code></td>
                                <td><code>string</code></td>
                                <td><code>'text'</code></td>
                                <td>Tipo de input HTML (text, email, password, etc.)</td>
                            </tr>
                            <tr>
                                <td><code>placeholder</code></td>
                                <td><code>string</code></td>
                                <td>-</td>
                                <td>Texto placeholder</td>
                            </tr>
                            <tr>
                                <td><code>disabled</code></td>
                                <td><code>boolean</code></td>
                                <td><code>false</code></td>
                                <td>Si el input está deshabilitado</td>
                            </tr>
                            <tr>
                                <td><code>readOnly</code></td>
                                <td><code>boolean</code></td>
                                <td><code>false</code></td>
                                <td>Si el input es solo lectura</td>
                            </tr>
                            <tr>
                                <td><code>required</code></td>
                                <td><code>boolean</code></td>
                                <td><code>false</code></td>
                                <td>Si el input es requerido</td>
                            </tr>
                            <tr>
                                <td><code>...props</code></td>
                                <td><code>InputHTMLAttributes</code></td>
                                <td>-</td>
                                <td>Cualquier prop HTML válida para input</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Import */}
            <section className={styles.section}>
                <h2>Importación</h2>
                <code className={styles.code}>
                    {`import { Input } from '@/shared/components/ui';`}
                </code>

                <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>
                    💡 También disponible como <code style={{ padding: '0.25rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '4px' }}>InputField</code> para compatibilidad con código existente.
                </p>
            </section>
        </div>
    );
};
